import React from 'react';
import axios from 'axios';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import dayjs from 'dayjs';
import Sidebar from '../components/ui/Sidebar';
import PageHeader from '../components/ui/PageHeader';
import ButtonComponent from '../components/ui/ButtonComponent';
import TextInputComponent from '../components/ui/TextInputComponent';
import SelectInputComponent from '../components/ui/SelectInputComponent';
import CardComponent from '../components/ui/CardComponent';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import {
  Box,
  Typography,
  Alert,
  FormControlLabel,
  Radio,
  RadioGroup,
  Checkbox,
  Paper,
  Divider,
  Switch,
  Slider,
  Tooltip,
} from '@mui/material';
import { useToast } from '../components/ui/ToastContext';
import { useAuth } from '../context/AuthContext';
import FormSkeleton from '../components/ui/FormSkeleton';

// ---- CF endpoints ----
const GET_FORM_DEF_URL = process.env.REACT_APP_GET_FORM_DEFINITION_URL || 'https://getformdefinition-774073159618.us-central1.run.app';
const SEARCH_CLIENTS_URL = process.env.REACT_APP_SEARCH_CLIENTS_URL || 'https://searchclients-774073159618.us-central1.run.app';
const GENERATE_PDF_URL = process.env.REACT_APP_GENERATE_FORM_PDF_URL || 'https://generateformpdf-774073159618.us-central1.run.app';
const SUBMIT_MINDBODY_URL = process.env.REACT_APP_SUBMIT_TO_MINDBODY_URL || 'https://submittomindbody-774073159618.us-central1.run.app';

// ---- helpers ----
const fieldDefaultValue = (field) => {
  if (Object.prototype.hasOwnProperty.call(field, 'default')) return field.default;
  switch (field.type) {
    case 'multiselect': return [];
    case 'checkbox':
    case 'switch': return false;
    case 'slider': return field.min ?? 0;
    case 'radio':
    case 'number':
    case 'email':
    case 'textarea':
    case 'select':
    case 'text': return '';
    case 'date': return null;
    default: return '';
  }
};

const formatFormDataForNotes = (data, schema) => {
  if (!data || !schema || !schema.noteSections) return "";
  const lines = [];
  const fieldMap = new Map(schema.fields.map(f => [f.name, f]));
  for (const section of schema.noteSections) {
    if (section.title && Array.isArray(section.fieldNames)) {
      lines.push(`${section.title}:`);
      for (const fieldName of section.fieldNames) {
        const field = fieldMap.get(fieldName);
        if (field) {
          const value = data[field.id] || 'N/A';
          lines.push(`  - ${field.label}: ${value}`);
        }
      }
      lines.push('');
    }
  }
  return lines.join('\n');
};

// --- RE-INTEGRATED HELPER FUNCTIONS ---
const toLabelCase = (str = "") =>
  String(str)
    .replace(/[_\-]+/g, " ")
    .replace(/([a-z09])([A-Z])/g, "$1 $2")
    .replace(/\s+/g, " ")
    .trim()
    .replace(/\b\w/g, (m) => m.toUpperCase());

const getFieldLabel = (field) =>
  field.label?.trim() ||
  (field.name ? toLabelCase(field.name) : toLabelCase(field.id));

const normalizeValue = (field, raw) => {
  if (raw == null) return "";
  switch (field.type) {
    case "checkbox":
    case "switch":
      return raw ? "Yes" : "No";
    case "multiselect":
      return Array.isArray(raw) ? raw.join(", ") : String(raw);
    case "date":
      return typeof raw === "string" ? raw : "";
    case "slider":
    case "number":
      return raw === "" ? "" : String(raw);
    default:
      return Array.isArray(raw) ? raw.join(", ") : String(raw);
  }
};

const buildDisplayData = (schema, values) => {
  const out = {};
  (schema?.fields || []).forEach((f) => {
    if (f.type === "section" || f.type === "divider") return;
    const label = getFieldLabel(f);
    out[label] = normalizeValue(f, values[f.id]);
  });
  return out;
};
// --- END OF RE-INTEGRATED FUNCTIONS ---


export default function FormFiller() {
  const { formId } = useParams();
  const navigate = useNavigate();
  const { addToast } = useToast();
  const { user, initializing } = useAuth();

  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState(null);
  const [formMeta, setFormMeta] = React.useState(null);
  const [version, setVersion] = React.useState(null);
  const [values, setValues] = React.useState({});
  const [errors, setErrors] = React.useState({});
  const [submitting, setSubmitting] = React.useState(false);

  const [pdfUrl, setPdfUrl] = React.useState(null);
  const [submittedData, setSubmittedData] = React.useState(null);
  const [selectedClient, setSelectedClient] = React.useState(null);
  const [clientQuery, setClientQuery] = React.useState('');
  const [clientResults, setClientResults] = React.useState([]);
  const [clientSearching, setClientSearching] = React.useState(false);
  const debounceRef = React.useRef(null);
  
  React.useEffect(() => {
    let mounted = true;
    const initFromDefinition = (def) => {
      const fields = def?.schema?.fields || [];
      const initVals = {};
      fields.forEach((f) => (initVals[f.id] = fieldDefaultValue(f)));

      setFormMeta({ id: def.id, name: def.name, description: def.description });
      setVersion({ id: def.currentVersion, schema: def.schema });
      setValues(initVals);
    };

    const run = async () => {
      if (initializing || !user) return;
      try {
        setLoading(true);
        setError(null);
        const idToken = await user.getIdToken(true);
        const { data } = await axios.post(GET_FORM_DEF_URL, { formId }, { headers: { Authorization: `Bearer ${idToken}` } });
        if (!mounted) return;
        const def = data?.form;
        if (!def) throw new Error('Form definition not found.');
        if (def.status !== 'published') {
          setError('This form is not currently published.');
          setLoading(false);
          return;
        }
        initFromDefinition(def);
      } catch (e) {
        if (!mounted) return;
        setError(e?.message || 'Failed to load form.');
      } finally {
        if (mounted) setLoading(false);
      }
    };
    run();
    return () => { mounted = false; };
  }, [initializing, user, formId]);
  
  React.useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);

    if (!clientQuery || clientQuery.trim().length < 2) {
      setClientResults([]);
      setClientSearching(false);
      return;
    }

    debounceRef.current = setTimeout(async () => {
      if (!user) return;
      try {
        setClientSearching(true);
        const idToken = await user.getIdToken(true);
        const { data } = await axios.post(
          SEARCH_CLIENTS_URL,
          { query: clientQuery.trim() },
          { headers: { Authorization: `Bearer ${idToken}` } }
        );
        setClientResults(data?.clients || []);
      } catch (e) {
        console.error('Client search failed:', e);
        setClientResults([]);
      } finally {
        setClientSearching(false);
      }
    }, 300);

    return () => clearTimeout(debounceRef.current);
  }, [clientQuery, user]);

  const handleClientSelect = (c) => {
    if (!c) return;
    setSelectedClient(c);
    setClientQuery('');
    setClientResults([]);
    addToast(`Client selected: ${c.fullName || c.email || c.id}`, 'info');
  };

  const clearSelectedClient = () => setSelectedClient(null);

  const isDisqualified = React.useMemo(() => {
    if (!version?.schema?.disqualifyingAnswers) return false;
    const { fields, disqualifyingAnswers } = version.schema;
    const fieldMap = new Map(fields.map(f => [f.name, f.id]));
    return Object.entries(disqualifyingAnswers).some(([fieldName, disqualifyingValue]) => {
      const fieldId = fieldMap.get(fieldName);
      return fieldId && values[fieldId] === disqualifyingValue;
    });
  }, [version, values]);

  const handleChange = (field, nextVal) => {
    setValues((prev) => ({ ...prev, [field.id]: nextVal }));
  };

  const validate = () => {
    const errs = {};
    (version?.schema?.fields || []).forEach((f) => {
      const v = values[f.id];

      if (f.required) {
        const empty =
          (Array.isArray(v) && v.length === 0) ||
          v === '' || v === null || v === undefined ||
          (typeof v === 'boolean' && v === false && f.type !== 'checkbox');
        if (empty) {
          errs[f.id] = `${f.label || f.id} is required`;
          return;
        }
      }
    });
    return errs;
  };

  const handleCopyToClipboard = () => {
    if (!submittedData) {
      addToast("No submitted data available to copy.", "warning");
      return;
    }
    
    const { submittedValues, clientInfo } = submittedData;
    const displayData = buildDisplayData(version.schema, submittedValues);
    const displayDataWithClient = {
        ...displayData,
        "Full Name": clientInfo.fullName || "",
        "Email": clientInfo.email || "",
        "Phone": clientInfo.phone || "",
    };

    const textToCopy = JSON.stringify(displayDataWithClient, null, 2);

    const textArea = document.createElement("textarea");
    textArea.value = textToCopy;
    textArea.style.position = "fixed";
    textArea.style.opacity = 0;
    document.body.appendChild(textArea);
    textArea.focus();
    textArea.select();
    try {
        document.execCommand('copy');
        addToast("Form data (JSON) copied to clipboard.", "success");
    } catch (err) {
        addToast("Failed to copy form data.", "error");
    }
    document.body.removeChild(textArea);
  };

  const onSubmit = async () => {
    if (!user) { addToast('You must be logged in to submit.', 'error'); return; }
    const vErrs = validate();
    setErrors(vErrs);
    if (Object.keys(vErrs).length > 0) { addToast('Please complete required fields.', 'warning'); return; }
    if (isDisqualified) {
      const confirmProceed = window.confirm("This client is disqualified based on their responses.\n\nAre you sure you want to submit this form?");
      if (!confirmProceed) return;
    }

    setSubmitting(true);
    const idToken = await user.getIdToken(true);

    try {
      const dataByName = Object.fromEntries(
        version.schema.fields.map(f => [f.name, values[f.id] || ''])
      );

      let clientFullName = selectedClient?.fullName;
      let clientEmail = selectedClient?.email;
      let clientPhone = selectedClient?.phone;

      if (!clientFullName) {
        clientFullName = dataByName.full_name || dataByName.fullName || dataByName.client_name;
      }
      if (!clientEmail) {
        clientEmail = dataByName.email_address || dataByName.email || dataByName.client_email;
      }
       if (!clientPhone) {
        clientPhone = dataByName.phone_number || dataByName.phone || dataByName.client_phone;
      }

      if (!clientFullName || !clientEmail) {
        addToast('A client must be associated with this form. Please search for and select a client in the header.', 'error');
        setSubmitting(false);
        return;
      }

      // --- UPDATED: Use your robust payload structure ---
      const displayData = buildDisplayData(version.schema, values);
      const displayDataWithClient = {
        ...displayData,
        "Full Name": clientFullName || "",
        "Email": clientEmail || "",
        "Phone": clientPhone || "",
      };

      const pdfPayload = {
        formId: formMeta.id,
        formData: values, // Raw id-keyed values for backend validation
        headerText: formMeta.name,
        schema: version.schema,
        displayData,
        displayDataWithClient,
        labelMap: Object.fromEntries(
          (version.schema.fields || [])
            .filter(f => f.type !== 'section' && f.type !== 'divider')
            .map(f => [f.id, getFieldLabel(f)])
        ),
        fullName: clientFullName,
        email: clientEmail,
        phone: clientPhone,
        clientData: { fullName: clientFullName, email: clientEmail, phone: clientPhone },
      };

      const pdfResponse = await axios.post(GENERATE_PDF_URL, pdfPayload, { headers: { Authorization: `Bearer ${idToken}` } });
      const pdfResult = pdfResponse.data;
      if (!pdfResult.downloadUrl || !pdfResult.path) {
        throw new Error(pdfResult.message || 'Failed to generate PDF.');
      }
      
      setPdfUrl(pdfResult.downloadUrl);
      addToast("Form submitted successfully! PDF is ready.", "success");

      const mindbodyResponse = await axios.post(
        SUBMIT_MINDBODY_URL,
        {
          ...dataByName,
          fullName: clientFullName,
          email: clientEmail,
          phone: clientPhone,
          pdfPath: pdfResult.path,
          formattedNotes: formatFormDataForNotes(values, version.schema),
          clientId: selectedClient?.id || null,
        },
        { headers: { Authorization: `Bearer ${idToken}` } }
      );

      if (mindbodyResponse.data?.mindbodyIntegrationEnabled !== false) {
         addToast("Client synced to Mindbody successfully.", "success");
      }
      
      setSubmittedData({
        submittedValues: values,
        clientInfo: {
          fullName: clientFullName,
          email: clientEmail,
          phone: clientPhone,
        }
      });
      
      const initVals = {};
      version.schema.fields.forEach((f) => (initVals[f.id] = fieldDefaultValue(f)));
      setValues(initVals);
      setSelectedClient(null);

    } catch (e) {
      console.error('Submit failed:', e);
      addToast(e.response?.data?.error || e.message || 'Failed to submit form.', 'error');
    } finally {
      setSubmitting(false);
    }
  };

  const renderRadio = (field) => {
    const currentValue = values[field.id] || '';
    const showInfo = field.infoOnValue && currentValue === field.infoOnValue.value;
    return (
      <Box key={field.id} sx={{ mb: 2 }}>
        <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
          {field.label || field.id} {field.required ? '*' : ''}
        </Typography>
        <RadioGroup
          row={!!field.inline}
          value={currentValue}
          onChange={(e) => handleChange(field, e.target.value)}
        >
          {(field.options || []).map((opt, idx) => {
            const optKey = `${field.id}-${opt.value || opt.label || idx}`;
            return (
              <FormControlLabel
                key={optKey}
                value={opt.value}
                control={<Radio />}
                label={opt.label}
              />
            );
          })}
        </RadioGroup>
        {showInfo && (
          <Box mt={1} p={2} bgcolor="#fff4e5" border="1px solid #ffc107" borderRadius={2}>
            <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
              {field.infoOnValue.text}
            </Typography>
          </Box>
        )}
        {(errors[field.id] || field.helperText) && (
          <Typography
            variant="caption"
            color={errors[field.id] ? 'error' : 'text.secondary'}
          >
            {errors[field.id] || field.helperText}
          </Typography>
        )}
      </Box>
    );
  };
  
  const renderField = (field) => {
    const common = {
      label: field.label || field.id,
      name: field.id,
      value: values[field.id],
      required: !!field.required,
      error: !!errors[field.id],
      helperText: errors[field.id] || field.helperText || '',
      placeholder: field.placeholder || '',
    };

    switch (field.type) {
      case 'divider':
        return (
          <Divider key={field.id} sx={{ my: 3 }}>
            <Typography variant="subtitle2" sx={{ px: 1 }}>
              {field.label}
            </Typography>
          </Divider>
        );

      case 'switch':
        return (
          <FormControlLabel
            key={field.id}
            sx={{ justifyContent: 'space-between', ml: 0.5, py: 1 }}
            labelPlacement="start"
            label={
              <Typography>
                {field.label || field.id} {field.required ? '*' : ''}
              </Typography>
            }
            control={
              <Switch
                checked={!!values[field.id]}
                onChange={(e) => handleChange(field, e.target.checked)}
              />
            }
          />
        );

      case 'slider':
        return (
          <Box key={field.id} sx={{ px: 1, pt: 1 }}>
            <Typography gutterBottom>
              {field.label || field.id} {field.required ? '*' : ''}
            </Typography>
            <Slider
              value={
                typeof values[field.id] === 'number'
                  ? values[field.id]
                  : (field.min ?? 0)
              }
              onChange={(e, newValue) => handleChange(field, newValue)}
              valueLabelDisplay="auto"
              min={field.min ?? 0}
              max={field.max ?? 100}
              step={field.step ?? 1}
            />
          </Box>
        );

      case 'textarea':
        return (
          <TextInputComponent
            key={field.id}
            {...common}
            multiline
            onChange={(e) => handleChange(field, e.target.value)}
          />
        );

      case 'number':
        return (
          <TextInputComponent
            key={field.id}
            {...common}
            type="number"
            onChange={(e) => handleChange(field, e.target.value)}
            inputProps={{
              min: typeof field.min === 'number' ? field.min : undefined,
              max: typeof field.max === 'number' ? field.max : undefined,
            }}
          />
        );

      case 'email':
        return (
          <TextInputComponent
            key={field.id}
            {...common}
            type="email"
            onChange={(e) => handleChange(field, e.target.value)}
          />
        );

      case 'date': {
        const dateValue = values[field.id] ? new Date(values[field.id]) : null;
        const datePickerComponent = (
          <DatePicker
            key={field.id}
            label={field.label || field.id}
            value={dateValue}
            onChange={(newDate) =>
              handleChange(
                field,
                newDate ? newDate.toISOString().split('T')[0] : null
              )
            }
            renderInput={(params) => (
              <TextInputComponent
                {...params}
                onKeyDown={(e) => e.preventDefault()}
                required={!!field.required}
                error={!!errors[field.id]}
              />
            )}
          />
        );
        if (field.helperText) {
          return (
            <Tooltip
              key={`${field.id}-tooltip`}
              title={field.helperText}
              placement="top-start"
              arrow
            >
              {datePickerComponent}
            </Tooltip>
          );
        }
        return datePickerComponent;
      }

      case 'select':
        return (
          <SelectInputComponent
            key={field.id}
            value={values[field.id] || ''}
            onChange={(e) => handleChange(field, e.target.value)}
            options={field.options || []}
            required={!!field.required}
            error={!!errors[field.id]}
            label={field.label || field.id}
            multiple={false}
          />
        );

      case 'multiselect':
        return (
          <SelectInputComponent
            key={field.id}
            value={values[field.id] || []}
            onChange={(e) => handleChange(field, e.target.value)}
            options={field.options || []}
            required={!!field.required}
            error={!!errors[field.id]}
            label={field.label || field.id}
            multiple
          />
        );

      case 'radio':
        return renderRadio(field);

      case 'checkbox':
        return (
          <Box key={field.id} sx={{ mb: 1 }}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={!!values[field.id]}
                  onChange={(e) => handleChange(field, e.target.checked)}
                />
              }
              label={field.label || field.id}
            />
            {(errors[field.id] || field.helperText) && (
              <Typography
                variant="caption"
                color={errors[field.id] ? 'error' : 'text.secondary'}
                sx={{ display: 'block', mt: -1 }}
              >
                {errors[field.id] || field.helperText}
              </Typography>
            )}
          </Box>
        );

      default:
        return (
          <TextInputComponent
            key={field.id}
            {...common}
            onChange={(e) => handleChange(field, e.target.value)}
          />
        );
    }
  };
  
  const groupedSections = React.useMemo(() => {
    if (!version?.schema?.fields) return [];
    const sections = [];
    let currentSection = { title: formMeta?.name || 'Form', description: formMeta?.description || 'Please complete the fields below.', fields: [] };
    sections.push(currentSection);
    for (const field of version.schema.fields) {
      if (field.type === 'section') {
        currentSection = { title: field.title, description: field.subtitle, fields: [] };
        sections.push(currentSection);
      } else {
        currentSection.fields.push(field);
      }
    }
    return sections;
  }, [version, formMeta]);

  if (initializing) {
    return (
      <div className="manage-users-container">
        <Sidebar />
        <div className="manage-users-content">
          <PageHeader title={formMeta?.name || 'Fill Form'} />
          <Box sx={{ display: 'flex', justifyContent: 'center', mt: 6 }}><FormSkeleton /></Box>
        </div>
      </div>
    );
  }

  const headerActions = selectedClient ? [<ButtonComponent key="clear-client" label="Clear client" type="secondary" size="small" onClick={clearSelectedClient} />] : null;

  return (
   <LocalizationProvider dateAdapter={AdapterDateFns}>
    <div className="manage-users-container">
      <Sidebar />
      <div className="manage-users-content">
        <PageHeader
          title={formMeta?.name || 'Fill Form'}
          showSearch
          searchTerm={clientQuery}
          onSearch={setClientQuery}
          results={clientResults}
          isSearching={clientSearching}
          placeholder="Search clients…"
          onSelectResult={handleClientSelect}
          actions={headerActions}
          renderResult={(c) => (<><strong>{c.fullName || 'Unnamed'}</strong>{c.email && <div className="search-meta">{c.email}</div>}</>)}
        />
        <Box sx={{ width: '100%', maxWidth: 900, mt: 4 }}>
          {loading ? <FormSkeleton /> : error ? <Alert severity="error">{error}</Alert> : (
            <>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, ...(submittedData && { display: 'none' }) }}>
                {isDisqualified && (
                  <Alert severity="error" sx={{ mb: 2 }}>
                    This client is disqualified based on their responses. Please review carefully before proceeding.
                  </Alert>
                )}
                {selectedClient && <Alert severity="info">Selected: {selectedClient.fullName}{selectedClient.email ? ` — ${selectedClient.email}` : ''} <em>(search in the header to change)</em></Alert>}
                
                {groupedSections.length === 0 ? <Alert severity="info">This form has no fields.</Alert> : (
                  groupedSections.map((section, index) => (
                    <Paper key={index} elevation={2} sx={{ p: 3 }}>
                      <Typography variant="h6" sx={{ mb: 1 }}>{section.title}</Typography>
                      {section.description && <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>{section.description}</Typography>}
                      <Box sx={{ display: 'grid', gap: 2.5 }}>{section.fields.map((f) => renderField(f))}</Box>
                    </Paper>
                  ))
                )}
                <Box sx={{ mt: 1, display: 'flex', gap: 1 }}>
                  <ButtonComponent label="Submit" type="primary" onClick={onSubmit} isLoading={submitting} />
                  <ButtonComponent label="Cancel" type="secondary" onClick={() => navigate(-1)} disabled={submitting} />
                </Box>
              </Box>

              {pdfUrl && submittedData && (
                <CardComponent>
                  <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                      <Typography variant="h6">Submission Complete</Typography>
                      <Box display="flex" gap={2}>
                        <ButtonComponent label="Copy Form Data" onClick={handleCopyToClipboard} />
                        <ButtonComponent label="Download PDF" onClick={() => window.open(pdfUrl, '_blank')} />
                      </Box>
                  </Box>
                  <Typography variant="subtitle2" sx={{ mb: 1 }}>
                    <strong>Date:</strong> {dayjs().format("MMMM D, YYYY")}
                  </Typography>
                  <Typography variant="body2" sx={{ color: "text.secondary" }}>
                      The form was successfully submitted and saved as a downloadable PDF.
                  </Typography>
                </CardComponent>
              )}
            </>
          )}
        </Box>
      </div>
    </div>
   </LocalizationProvider> 
  );
}